import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../../utility/axiosInstance';
import { handleAxiosError } from '../../helpers/handleAxiosError';

export interface Snapshot {
    id: string;
    name: string;
    status: string;
    size: number;
    created_at: string;
    instance_id?: string;
}

interface SnapshotsState {
    snapshots: Snapshot[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: SnapshotsState = {
    snapshots: [],
    status: 'idle',
    error: null,
};

export const fetchSnapshots = createAsyncThunk(
    'snapshots/fetchSnapshots',
    async (_, { rejectWithValue }) => {
        console.log("🔥 fetchSnapshots thunk triggered");

        try {
            const response = await axiosInstance.get('/v1/snapshots');

            if (!Array.isArray(response.data)) {
                throw new Error("Expected array of snapshots");
            }

            return response.data;
        } catch (error) {
            console.error("Failed to fetch snapshots:", error);
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const snapshotsSlice = createSlice({
    name: 'snapshots',
    initialState,
    reducers: {
        resetSnapshots: () => initialState,
        addSnapshot: (state, action: PayloadAction<Snapshot>) => {
            state.snapshots.push(action.payload);
        },
        removeSnapshot: (state, action: PayloadAction<{ id: string }>) => {
            state.snapshots = state.snapshots.filter(snap => snap.id !== action.payload.id);
        },
        updateSnapshot: (state, action: PayloadAction<Snapshot>) => {
            const index = state.snapshots.findIndex(
                (snapshot) => snapshot.id === action.payload.id
            );

            if (index !== -1) {
                state.snapshots[index] = action.payload;
            } else {
                state.snapshots.push(action.payload);
            }
        },
        setSnapshotStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
            const snapshot = state.snapshots.find(s => s.id === action.payload.id);
            if (snapshot) {
                snapshot.status = action.payload.status;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSnapshots.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchSnapshots.fulfilled, (state, action: PayloadAction<Snapshot[]>) => {
                state.status = 'succeeded';
                state.snapshots = action.payload.map(snap => ({
                    id: snap.id,
                    name: snap.name,
                    status: snap.status,
                    size: snap.size || 0,
                    created_at: snap.created_at,
                    instance_id: snap.instance_id,
                }));
            })
            .addCase(fetchSnapshots.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
                state.snapshots = [];
            });
    },
});

export const { resetSnapshots, addSnapshot, removeSnapshot, setSnapshotStatus, updateSnapshot } = snapshotsSlice.actions;
export default snapshotsSlice.reducer;